import React, { useMemo } from 'react';

import styles from './LogRowExpender.module.css';

import Common, { getEnumLabelByValue } from '@silverfort-nodelibs/common/utils/common';
import {
    AD_ENTITY_TYPES,
    AUTH_MODE_FILTERS_OPTIONS,
    AUTHENTICATION_LOG_REASONS,
    AUTHENTICATOR_TYPES,
    AZURE_SSO_AUTHENTICATION_VALUE,
    DIRECTORY_TYPES,
    FEATURE_FLAGS_NAMES,
    HTTP_OPTIONS,
    ROLES,
    RULES_TYPES
} from '@silverfort-nodelibs/common/utils/consts';
import { httpPost, isUserPermitted } from '../utils';

import useFeature from '../SystemSettings/hooks/featureFlags/useFeature';

import riskLow from '../common/Images/risk_low.png';
import riskMedium from '../common/Images/risk_medium.png';
import riskHigh from '../common/Images/risk_high.png';
import riskCritical from '../common/Images/risk_critical.png';
import { getIconPathByRiskIndicator, getRiskIndicatorByRiskRule } from '@silverfort-nodelibs/common/utils/insight';
import { getIconFromIconPath } from '../Insights/insights-utils';
import { SYSTEM_MODES } from '@silverfort-nodelibs/common/utils/system-mode-config';
import { isEmpty } from 'lodash';
import { FeatureLock, SHOWSTOPPER_PROP_OPTIONS } from '../common/LockedFeatures';
import { DisabledDisplayOptions, RequiredModuleState } from '../SystemSettings/hooks/lockedFeatures/types';
import { PricingModules, usePricingModulesState } from '../../context/contexts/PricingModuleContext';
import { isAnyEnablingModulesActive } from '../SystemSettings/hooks/lockedFeatures/utils';
import investigateIcon from '../common/Images/investigation_blue.png';
import InvestigationDrawer from '../InvestigationDrawer/InvestigationDrawer';
import { enableRisk } from './consts';
import { Alert, showAlert } from '@silverfort/sf-components';

const RISK_ICONS = {
    LOW: riskLow,
    MEDIUM: riskMedium,
    HIGH: riskHigh,
    CRITICAL: riskCritical
};

const CreatePolicyButton = ({
    isInternalServiceAccount,
    onCreatePolicyClicked,
    disabledBtnTitle,
    isCreatePolicyButtonDisabled,
    showPoliciesShowstopper
}) => {
    const featureLockDisplayOptions = useMemo(
        () => ({
            enabled: !showPoliciesShowstopper,
            ...(showPoliciesShowstopper && {
                disabledDisplay: DisabledDisplayOptions.SHOWSTOPPER,
                showstopperProps: SHOWSTOPPER_PROP_OPTIONS.POLICIES
            })
        }),
        [showPoliciesShowstopper]
    );

    return isInternalServiceAccount ? (
        <div className={styles.buttonsSection}>
            <div
                className={`${styles.createPolicyBtn} ${styles.disabledBtn}`}
                onClick={onCreatePolicyClicked}
                title={disabledBtnTitle}>
                Silverfort Internal Authentication
            </div>
        </div>
    ) : (
        <div className={styles.buttonsSection}>
            <FeatureLock displayOptions={featureLockDisplayOptions}>
                <div
                    className={`${styles.createPolicyBtn} ${isCreatePolicyButtonDisabled ? styles.disabledBtn : ''}`}
                    onClick={onCreatePolicyClicked}
                    title={disabledBtnTitle}>
                    Create Policy
                </div>
            </FeatureLock>
        </div>
    );
};

const LogRowExpender = ({
    logData,
    rulesNames,
    wallNames,
    user,
    allowDesktopMfa,
    countriesList,
    hiddenRiskIndicators,
    investigationView,
    blackBackground,
    ssoVendors,
    groupPolicies,
    appDrawerContext
}) => {
    const geoLocationEnabled = useFeature(FEATURE_FLAGS_NAMES.GeoLocationEnabled);
    const isSaasMode = useFeature(FEATURE_FLAGS_NAMES.SystemMode) === SYSTEM_MODES.SAAS;
    const { pricingModulesState } = usePricingModulesState();

    const showPoliciesShowstopper =
        pricingModulesState &&
        !isAnyEnablingModulesActive(
            {
                [PricingModules.MFA_USERS]: RequiredModuleState.ON_PREM_ENABLED,
                [PricingModules.AUTHENTICATION_FIREWALL]: RequiredModuleState.ON_PREM_ENABLED
            },
            pricingModulesState
        );

    const ruleIdWithPrefix =
        logData.effective_rule_id && logData.effective_rule_type
            ? Common.getRuleIdWithPrefix(logData.effective_rule_id, logData.effective_rule_type)
            : '';

    const isNtlmFailOpen =
        logData.protocol_type.toLowerCase() === 'ntlm' &&
        logData.user_response.toLowerCase() === AUTHENTICATION_LOG_REASONS.FAIL_OPEN.USER_RESPONSE &&
        !ruleIdWithPrefix;

    const isMatchingPolicyMissing =
        !ruleIdWithPrefix ||
        (ruleIdWithPrefix && rulesNames[ruleIdWithPrefix] && rulesNames[ruleIdWithPrefix].is_deleted);

    const isCreatePolicyButtonDisabled =
        (!allowDesktopMfa && logData.protocol_type.toLowerCase() === 'desktop') ||
        (logData.effective_rule_type && !(logData.effective_rule_type === RULES_TYPES.REGULAR)) ||
        !isUserPermitted(user, 'policies', ROLES.EDITOR.value) ||
        logData.protocol_type.toLowerCase() === 'api' ||
        (!logData.user_guid && !!logData.external_user_id);

    const isInternalServiceAccount = logData.is_internal_auth;

    const isShowPolicyButtonDisabled =
        !(logData.effective_rule_type === RULES_TYPES.REGULAR) ||
        !isUserPermitted(user, 'policies', ROLES.VIEWER.value);

    let disabledBtnTitle = null;
    if (isCreatePolicyButtonDisabled) {
        disabledBtnTitle =
            !logData.user_guid && !!logData.external_user_id
                ? 'Guest accounts cannot be protected via Silverfort policies'
                : 'Policy cannot be created from this log';
    } else if (isInternalServiceAccount) {
        disabledBtnTitle = 'Can not create policy based on Silverfort authentication';
    }
    const onShowPolicyClicked = () => {
        !isShowPolicyButtonDisabled && handleOpenPolicy();
    };
    const onCreatePolicyClicked = () => {
        const isCreatePolicyPossible =
            !isMatchingPolicyMissing || !(isCreatePolicyButtonDisabled || isInternalServiceAccount);
        isCreatePolicyPossible && handleOpenPolicy();
    };
    const handleOpenPolicy = async () => {
        let effectiveRuleId = logData.effective_rule_id;
        try {
            if (effectiveRuleId && rulesNames[effectiveRuleId] && rulesNames[effectiveRuleId].is_deleted) {
                effectiveRuleId = '';
            }
            await httpPost(
                '/Logs/openPolicyFromLog',
                {
                    logRow: { ...logData, effective_rule_id: effectiveRuleId }
                },
                { [HTTP_OPTIONS.REDIRECT_TO_NEW_TAB]: true }
            );
        } catch (err) {
            console.error('Getting logs failed', err.toString());
            showAlert('Something went wrong, please try again later', 'error');
        }
    };

    const ruleName = Common.formatRuleName(
        logData.effective_rule_id,
        logData.effective_rule_type,
        rulesNames,
        logData.group_policy_id,
        groupPolicies
    );

    const [userResponse] = Common.formatUserResponse(logData.user_response, logData.effective_rule_action);

    let serverDomain = logData.server_domain;
    if (serverDomain) {
        serverDomain = serverDomain.toLowerCase();
        if (serverDomain === 'n/a') {
            serverDomain = '';
        }
    }

    const domainController = logData.domain_controller
        ? logData.domain_controller.toUpperCase()
        : logData.domain_controller;
    const mfaTime = Common.formatMFATime(logData.user_response_interval) || 'n/a';
    const alert = logData.alert ? 'Yes' : 'No';

    let vendorName = '';
    let authenticator = Common.formatAuthenticator(logData.authenticator) || 'n/a';

    if (logData.authenticator && AUTHENTICATOR_TYPES.BRIDGE_SSO.value === logData.authenticator) {
        //In case of an old wall, we will display a default value
        if (!logData.vendor_id) {
            vendorName = 'Entra ID';
        } else {
            vendorName = ssoVendors[logData.vendor_id]?.name || 'n/a';
        }
    }
    // TODO: Remove on version 5.3
    // This `if` statement ensures backward compatibility. For the bridge authenticator logs,
    // we set 'vendorName' and 'authenticator' for 'azure_sso' logs.
    if (logData.authenticator === AZURE_SSO_AUTHENTICATION_VALUE) {
        vendorName = 'Entra ID';
        authenticator = AUTHENTICATOR_TYPES.BRIDGE_SSO.label;
    }

    const retrospectiveAnswer = Common.getWasItYouDisplayNameByLog(logData);

    const displayRiskIndicators = pricingModulesState ? enableRisk(pricingModulesState) : false;
    let riskIndicators = [];

    const getRiskIndicatorTitle = (riskIndicatorObj) => {
        let riskIndicatorTitle = riskIndicatorObj.authTitle;
        const extraDetails =
            !isEmpty(riskIndicatorObj.extraLogDetails) &&
            Common.getExtraRiskIndicatorDescription(
                logData,
                riskIndicatorObj.extraLogDetails.extraDetailsType,
                riskIndicatorObj.extraLogDetails.extraDetailsField
            );

        if (!isEmpty(extraDetails)) {
            riskIndicatorTitle += ` (${extraDetails})`;
        }
        return riskIndicatorTitle;
    };

    if (logData.risk_indicators_v2) {
        const filteredRiskIndicators = logData.risk_indicators_v2.filter(
            (riskIndicator) => !hiddenRiskIndicators.includes(riskIndicator)
        );

        const riskIndicatorsList = filteredRiskIndicators.flatMap((riskRule) => {
            const riskIndicator = getRiskIndicatorByRiskRule(riskRule);
            return riskIndicator ? riskIndicator : [];
        });

        riskIndicators = riskIndicatorsList.map((riskIndicatorObj, index) => {
            const riskIndicatorTitle = getRiskIndicatorTitle(riskIndicatorObj);
            return (
                <div key={`risk_${riskIndicatorObj.authTitle}_${index}`} className={styles.riskIndicator}>
                    <img
                        className={styles.riskIndicatorImg}
                        src={getIconFromIconPath(getIconPathByRiskIndicator(riskIndicatorObj))}
                        alt=""
                    />
                    <span>{riskIndicatorTitle}</span>
                </div>
            );
        });
    }

    const directoryType =
        logData.directory_type && DIRECTORY_TYPES[logData.directory_type]
            ? DIRECTORY_TYPES[logData.directory_type].key
            : DIRECTORY_TYPES.ad.key;

    const nodeName = wallNames[logData.wall_id]
        ? `${wallNames[logData.wall_id]} (${logData.wall_id})`
        : logData.wall_id;

    const locationText = Common.isFeatureActive(geoLocationEnabled)
        ? Common.getLocationText(logData, countriesList)
        : '';

    let ruleAction = Common.formatRuleAction(
        logData.effective_rule_action,
        logData.effective_rule_id,
        true,
        logData.protocol_type
    );

    const displayUserResponse = userResponse && (userResponse !== 'n/a' || ruleAction === 'MFA');
    if (ruleAction && displayUserResponse) {
        ruleAction += ` (${userResponse})`;
    }

    if (!ruleAction) {
        ruleAction = 'None';
    }

    const riskLevelName = Common.riskLevelToText(logData.risk_level);

    let mfaUserName = '';
    let mfaUserGuid = '';
    if (directoryType === DIRECTORY_TYPES.local.key && logData.protocol_type.toLowerCase() === 'desktop') {
        if (appDrawerContext && logData.mfa_response_user_guid) {
            mfaUserGuid = logData.mfa_response_user_guid;
        }

        mfaUserName = logData.mfa_response_user_name || logData.desktop_mfa_username;
    }

    return (
        <>
            <div
                className={`${styles.logDetails} ${investigationView ? styles.investigation : ''} ${
                    blackBackground ? styles.blackBackground : ''
                }`}>
                <div className={styles.detailsSections}>
                    {investigationView && (
                        <div className={styles.detailsSection}>
                            <div className={styles.sectionHeader}>
                                <span>Auth</span>
                            </div>
                            <div className={styles.sectionData}>
                                <div className={styles.label}>Risk:</div>
                                <div className={`${styles.value} ${styles.riskContainer}`} title={riskLevelName}>
                                    {riskLevelName}
                                    <img alt={riskLevelName} src={RISK_ICONS[riskLevelName.toUpperCase()]} />
                                </div>
                            </div>
                            <div className={styles.sectionData}>
                                <div className={styles.label}>Auth Type:</div>
                                <div
                                    className={styles.value}
                                    title={`${DIRECTORY_TYPES[directoryType].displayName} (${logData.protocol_type})`}>
                                    {`${DIRECTORY_TYPES[directoryType].displayName} (${logData.protocol_type})`}
                                </div>
                            </div>

                            <div className={styles.sectionData}>
                                <div className={styles.label}>Silverfort Action:</div>
                                <div className={styles.value} title={ruleAction}>
                                    {`${ruleAction}`}
                                </div>
                            </div>
                        </div>
                    )}
                    <div className={styles.detailsSection}>
                        <div className={styles.sectionHeader}>
                            <span>Route</span>
                        </div>
                        {domainController &&
                            directoryType !== DIRECTORY_TYPES.aad.key &&
                            directoryType !== DIRECTORY_TYPES.okta.key &&
                            directoryType !== DIRECTORY_TYPES.pingfed.key && (
                                <div className={styles.sectionData}>
                                    <div className={styles.label}>Domain Controller:</div>
                                    <div className={styles.value} title={domainController}>
                                        {domainController}
                                    </div>
                                </div>
                            )}
                        {directoryType === DIRECTORY_TYPES.aad.key && logData.tenant_id && (
                            <div className={styles.sectionData}>
                                <div className={styles.label}>Tenant ID:</div>
                                <div className={styles.value} title={logData.tenant_id}>
                                    {logData.tenant_id}
                                </div>
                            </div>
                        )}
                        {nodeName && !isSaasMode && (
                            <div className={styles.sectionData}>
                                <div className={styles.label}>Silverfort Node:</div>
                                <div className={styles.value} title={nodeName}>
                                    {nodeName}
                                </div>
                            </div>
                        )}
                        {Common.isFeatureActive(geoLocationEnabled) && locationText && (
                            <div className={styles.sectionData}>
                                <div className={styles.label}>Location:</div>
                                <div className={styles.value} title={locationText}>
                                    {locationText}
                                </div>
                            </div>
                        )}
                        <div className={styles.sectionData}>
                            <div className={styles.label}>Request ID:</div>
                            <div className={styles.value} title={logData.request_id}>
                                {logData.request_id}
                            </div>
                        </div>
                        {(isNtlmFailOpen || logData.auth_mode) && (
                            <div className={styles.sectionData}>
                                <div className={styles.label}>Mode:</div>
                                <div className={styles.value}>
                                    {getEnumLabelByValue(AUTH_MODE_FILTERS_OPTIONS, logData.auth_mode)}
                                </div>
                            </div>
                        )}
                    </div>
                    {logData.effective_rule_id && (
                        <div className={styles.detailsSection}>
                            <div className={styles.sectionHeader}>
                                <span>Policy</span>
                            </div>
                            <div className={styles.sectionData}>
                                <div className={styles.label}>Name:</div>
                                {!investigationView && (
                                    <div className={styles.value} title={ruleName}>
                                        {ruleName}
                                    </div>
                                )}
                                {investigationView && (
                                    <div
                                        className={`${styles.value} ${!isShowPolicyButtonDisabled && styles.link}`}
                                        title={ruleName}
                                        onClick={onShowPolicyClicked}>
                                        {ruleName}
                                    </div>
                                )}
                            </div>
                            <div className={styles.sectionData}>
                                <div className={styles.label}>MFA Response:</div>
                                <div className={styles.value} title={userResponse}>
                                    {userResponse}
                                </div>
                            </div>
                            <div className={styles.sectionData}>
                                <div className={styles.label}>MFA Time:</div>
                                <div className={styles.value} title={mfaTime}>
                                    {mfaTime}
                                </div>
                            </div>
                            {mfaUserName && (
                                <div className={styles.sectionData}>
                                    <div className={styles.label}>AD User:</div>
                                    <div
                                        className={`${styles.value} ${mfaUserGuid ? styles.topRow : ''}`}
                                        title={mfaUserName}>
                                        {mfaUserName}
                                        {mfaUserGuid && (
                                            <div className={styles.investigateWarper}>
                                                <div
                                                    className={styles.investigate}
                                                    title={'Investigate'}
                                                    onClick={() =>
                                                        appDrawerContext.displayContent(
                                                            <InvestigationDrawer
                                                                guid={mfaUserGuid}
                                                                identifier={mfaUserName}
                                                                adType={AD_ENTITY_TYPES.USER}
                                                            />
                                                        )
                                                    }>
                                                    <img alt="investigate" src={investigateIcon} />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                            {logData.token_id && (
                                <div className={styles.sectionData}>
                                    <div className={styles.label}>Token ID:</div>
                                    <div className={styles.value} title={logData.token_id}>
                                        {logData.token_id}
                                    </div>
                                </div>
                            )}
                            <div className={styles.sectionData}>
                                <div className={styles.label}>Authenticator:</div>
                                <div className={styles.value} title={authenticator}>
                                    {authenticator}
                                </div>
                            </div>
                            {vendorName && (
                                <div className={styles.sectionData}>
                                    <div className={styles.label}>IdP:</div>
                                    <div className={styles.value} title={vendorName}>
                                        {vendorName}
                                    </div>
                                </div>
                            )}
                            {!investigationView && (
                                <div className={styles.sectionData}>
                                    <div className={styles.label}>Alert:</div>
                                    <div className={styles.value} title={alert}>
                                        {alert}
                                    </div>
                                </div>
                            )}
                            {retrospectiveAnswer && !investigationView && (
                                <div className={styles.sectionData}>
                                    <div className={styles.label}>"Was it you" answer:</div>
                                    <div className={styles.value} title={retrospectiveAnswer}>
                                        {retrospectiveAnswer}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                    {riskIndicators.length > 0 && (
                        <FeatureLock
                            displayOptions={{
                                enabled: displayRiskIndicators,
                                disabledDisplay: displayRiskIndicators ? undefined : DisabledDisplayOptions.HIDDEN
                            }}>
                            <div className={`${styles.riskIndicatorsSection} ${styles.detailsSection}`}>
                                <div className={styles.sectionHeader}>
                                    <span>Risk</span>
                                </div>
                                <div className={styles.sectionData}>
                                    <div className={styles.label}>Risk Indicators:</div>
                                    <div className={`${styles.value} ${styles.riskIndicatorsContainer}`}>
                                        {riskIndicators}
                                    </div>
                                </div>
                            </div>
                        </FeatureLock>
                    )}
                </div>
                {!investigationView &&
                    (isMatchingPolicyMissing ? (
                        <CreatePolicyButton
                            isInternalServiceAccount={isInternalServiceAccount}
                            isCreatePolicyButtonDisabled={isCreatePolicyButtonDisabled}
                            onCreatePolicyClicked={onCreatePolicyClicked}
                            disabledBtnTitle={disabledBtnTitle}
                            showPoliciesShowstopper={showPoliciesShowstopper}
                        />
                    ) : (
                        <div className={styles.buttonsSection}>
                            <div
                                className={`${styles.createPolicyBtn} ${
                                    isShowPolicyButtonDisabled ? styles.disabledBtn : ''
                                }`}
                                onClick={onShowPolicyClicked}
                                title={disabledBtnTitle}>
                                Show Policy
                            </div>
                        </div>
                    ))}
            </div>
            <Alert />
        </>
    );
};

export default LogRowExpender;
